<script setup lang="ts">
import dayjs from 'dayjs'
import { computed } from 'vue'
import { NXGDSTable, NXGDSStatusTag, NXGDSTooltip } from '@nsftx/seven-nxg-design-system'
import {
  useLatestBetslipsDataTable,
  type LatestBetslipsDataTable,
} from '@/composables/useLatestBetslipsTable/useLatestBetslipsTable'
import SSLatestBetslipsActions from './SSLatestBetslipsActions.vue'
import { useLatestBetslipActions } from '@/composables/useLatestBetslipActions/useLatestBetslipActions'

interface LatestBetslipsTableProps {
  latestBetslipsDataTable: LatestBetslipsDataTable[]
}

const { latestBetslipsDataTable } = defineProps<LatestBetslipsTableProps>()

const BETSLIP_TIME_FORMAT_FULL = 'HH:mm:ss DD.MM.YYYY'
const BETSLIP_TIME_FORMAT_SHORT = 'HH:mm DD/MM'

const { latestBetslipsHeaders } = useLatestBetslipsDataTable()
const {
  latestBetslipsTable,
  betslipActions,
  betslipActionsPosition,
  showBetslipActions,
  hideBetslipActions,
} = useLatestBetslipActions()

const betslipActionsStyle = computed(() => ({
  left: `${betslipActionsPosition.value.left}px`,
  top: betslipActionsPosition.value.top != null ? `${betslipActionsPosition.value.top}px` : 'auto',
  bottom:
    betslipActionsPosition.value.bottom != null
      ? `${betslipActionsPosition.value.bottom}px`
      : 'auto',
}))

const showBetslipActionsMenu = (
  { feBetslipId }: LatestBetslipsDataTable,
  _rowIndex: number,
  event: Event,
) => {
  const tableContainer = latestBetslipsTable.value

  if (tableContainer) {
    showBetslipActions(tableContainer, feBetslipId, event)
  }
}
</script>

<template>
  <div ref="latestBetslipsTable" class="ss-latest-betslips-table">
    <NXGDSTable
      :columns="latestBetslipsHeaders"
      :rows="latestBetslipsDataTable"
      hover
      sticky
      context-menu
      max-height="100%"
      @click:row="showBetslipActionsMenu"
      @contextmenu:row="showBetslipActionsMenu"
      class="ss-latest-betslips-table__inner"
    >
      <template #indicator="{ value }">
        <span
          class="ss-latest-betslips-table__inner__indicator"
          :style="{ backgroundColor: String(value) }"
        ></span>
      </template>
      <template #time="{ value }">
        <div class="ss-latest-betslips-table__inner__time">
          <NXGDSTooltip
            :text="dayjs(Number(value)).format(BETSLIP_TIME_FORMAT_FULL)"
            placement="right"
            :placement-offset="{ mainAxis: 10 }"
          >
            <template #reference>
              {{ dayjs(Number(value)).format(BETSLIP_TIME_FORMAT_SHORT) }}
            </template>
          </NXGDSTooltip>
        </div>
      </template>
      <template #status="{ value }">
        <NXGDSStatusTag v-bind="value" />
      </template>
    </NXGDSTable>
    <SSLatestBetslipsActions
      v-if="betslipActions.length"
      :actions="betslipActions"
      @click:action="hideBetslipActions"
      class="ss-latest-betslips-table__actions"
      :style="betslipActionsStyle"
    />
  </div>
</template>

<style scoped lang="scss">
.ss-latest-betslips-table {
  position: relative;
  min-height: 0;

  &__inner {
    &__indicator {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 2px;
    }

    &__time {
      width: max-content;
    }
  }

  &__actions {
    position: absolute;
  }
}
</style>
